import React, { useEffect, useState } from 'react'
import { ReduxRouter } from '@lagunovsky/redux-react-router'
import { IntlProvider } from 'react-intl';

import {
  navConfig,
  dismissAlert,
  hideAllAlerts,
  localeMessages,
  storeHistory,
  setDarkModeTheme,
  FeedbackContainer,
  OopsContainer,
  AuthExpiryContainer,
  appInsights,
  PortalPrereqContainer,
  PendingRedirectContainer,
  useAppDispatch,
  useAppSelector,
  addPendingRedirect,
  ENavPages
} from '../internal'

import {
  environment,
  buildId,
  cdpVersion,
  commonsVersion,
  releaseId
} from '@alias-environment';

import {
  keepUiTheme,
  UniConditionalRender,
  UniBanner,
  UniMultiToast,
  IUniToast_Alert,
  UniErrorBoundary,
  UniRoutes,
  useSystemThemeListener,
  AuthUserC,
} from '@unikey/unikey-commons/release/comm';

interface IProps {
  i18nConfig: any,
  alerts: Map<number, IUniToast_Alert>,
  alertReview: boolean,
  alertUpdate: number,
  feedbackModalOpen?: boolean,
  
  handleLanguageChange(localeCode: string): void,
  dismissAlert(alertId: number): void
  deleteAlert(alertId: number): void,
  hideAllAlerts(): void,
  setThemeMode(theme: string): void
}

const AppContainer: React.FC<IProps> = (props) => {

  const dispatch = useAppDispatch();
  const systemTheme = useSystemThemeListener();
  
  const $i18nConfig: any = useAppSelector(state => state.i18n);
  const $alerts: Map<number, IUniToast_Alert> = useAppSelector(state => state.alerts.showingAlerts);
  const $alertReview: boolean = useAppSelector(state => state.alerts.review);
  const $currentUser: AuthUserC = useAppSelector(state => state.authenticatedUser.currentUser);
  // We arent using numAlerts in this component, but changes to the alerts Map does not trigger a re-render here. We are forcing a re-render here by listening to the numAlerts variable
  const $alertUpdate: number = useAppSelector(state => state.alerts.alertUpdate);
  const $darkModeTheme = useAppSelector(state => state.portal.darkModeTheme);
  const $feedbackModalOpen = useAppSelector(state => state.feedbackModal.modalOpen);

  useEffect(() => {
    const initialTheme = keepUiTheme();
    dispatch(setDarkModeTheme(initialTheme));
  }, [systemTheme]);
  
  const _closeAlertHistory = () => {
    dispatch(hideAllAlerts());
  }

  const _handleLocaleErrors = (errMsg: any) => {
    if (environment === 'development') {
      console.warn(errMsg);
    }
  }
  
  const _logToAppInsights = (msg: string) => {
     const user = $currentUser?.getBasicProfile?.();
      appInsights.trackEvent({ name: 'portalError' }, 
      {
        content: msg,
        message: msg,
        username: user?.username?.value,
        userId: user?.id,
        location: window.location.href,
      });
      return Promise.resolve();
  }
    
  return (
      <IntlProvider
        key={$i18nConfig.localeCode}
        locale={$i18nConfig.localeCode}
        defaultLocale={$i18nConfig.localeCode}
        messages={localeMessages.get($i18nConfig.localeCode)}
        onError={_handleLocaleErrors}>
        <>
          <ReduxRouter history={storeHistory}>
            
            <UniErrorBoundary
                errorRender={() => (<OopsContainer />)} 
                goToErrorPage={() => { dispatch(addPendingRedirect({ pathname: navConfig.get(ENavPages.oopsReason)!.linkTo(['404']), priority: 'low' })) }}
                log={_logToAppInsights}>

              <UniRoutes
                navConfig={navConfig} 
                portalContainer={<PortalPrereqContainer />} />

              <PendingRedirectContainer history={storeHistory} />

            </UniErrorBoundary>
          </ReduxRouter>

          <UniConditionalRender visible={environment === 'development'}>
            <AuthExpiryContainer />
          </UniConditionalRender>

          <UniConditionalRender visible={$feedbackModalOpen}>
            <FeedbackContainer />
          </UniConditionalRender>

          <UniConditionalRender visible={environment === 'staging' || environment === 'development'}>
            <UniBanner
              type={environment}
              messageKey={`${environment}Environment`}
              secondaryMessageKey={`RELEASE[${releaseId.split('-').pop()}] / C[${commonsVersion}] / CDP[${cdpVersion}]`} />
          </UniConditionalRender>

          <section className="toasts">
            <UniMultiToast
              alerts={$alerts}
              dismissAlert={(alertId) => dispatch(dismissAlert(false, alertId))}
              deleteAlert={(alertId) => dispatch(dismissAlert(true, alertId))}
              inReview={$alertReview}
              backdropClick={_closeAlertHistory} />
          </section>
        </>
      </IntlProvider>
    )
  }

  export default AppContainer